import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Badge, Button, Card, CardHeader } from '@/components/ui';
import { cn } from '@/lib/cn';
import { assetsApi } from '@/lib/api/assets';
import { useI18n } from '@/i18n';
import type { ExpiringAsset } from '@/types/assets';

export function AssetOverviewTab() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data, error, isLoading } = useQuery({
    queryKey: ['assets', 'overview'],
    queryFn: assetsApi.getOverview,
  });
  const sync = useMutation({
    mutationFn: assetsApi.triggerSync,
    onSettled: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['assets', 'overview'] }),
        qc.invalidateQueries({ queryKey: ['assets', 'items'] }),
        qc.invalidateQueries({ queryKey: ['assets', 'sync-status'] }),
      ]);
    },
  });

  if (isLoading) {
    return <div className="p-6 text-muted-foreground">{t('common.loading')}</div>;
  }

  if (error || !data) {
    return <div className="p-6 text-destructive">{t('assetsPage.loadFailed')}</div>;
  }

  const hasWarning = !data.validation_ok || data.range_truncated || Boolean(data.last_error);
  const syncErrorMessage = sync.error ? getErrorMessage(sync.error) : null;

  return (
    <div className="space-y-6">
      {hasWarning && (
        <div className="flex gap-2 rounded-lg border border-warning bg-warning/10 p-3 text-sm text-foreground">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning" />
          <div>
            <div className="font-medium">{t('assetsPage.validationWarn')}</div>
            {data.validation_note && <div className="mt-1">{data.validation_note}</div>}
            {data.range_truncated && <div className="mt-1">{t('assetsPage.rangeTruncatedWarn')}</div>}
            {data.last_error && <div className="mt-1">{data.last_error}</div>}
          </div>
        </div>
      )}

      {syncErrorMessage && (
        <div className="flex gap-2 rounded-lg border border-destructive bg-destructive/10 p-3 text-sm text-foreground">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" />
          <div>
            <div className="font-medium">{t('assetsPage.syncFailed')}</div>
            <div className="mt-1">{syncErrorMessage}</div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-4">
        <div className="text-sm text-muted-foreground">
          {t('assetsPage.lastSync')}: {data.last_sync_finished_at ?? '-'} ({data.last_sync_status})
        </div>
        <Button
          size="sm"
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
        >
          <RefreshCw className={cn('mr-2 h-4 w-4', sync.isPending && 'animate-spin')} />
          {sync.isPending ? t('assetsPage.syncing') : t('assetsPage.syncNow')}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard label={t('assetsPage.totalAssets')} value={String(data.total_count)} />
        <MetricCard label={t('assetsPage.recordsInCache')} value={String(data.item_count)} />
        <MetricCard label={t('assetsPage.certificates')} value={String(data.by_type.certificate ?? 0)} />
        <MetricCard
          label={t('assetsPage.expiringWithinYear')}
          value={String(data.expiring.length)}
          highlight={data.expiring.some((item) => item.severity !== 'normal')}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <DistributionCard title={t('assetsPage.byType')} data={data.by_type} unknown={t('assetsPage.unknown')} />
        <DistributionCard
          title={t('assetsPage.byEnvironment')}
          data={data.by_environment}
          unknown={t('assetsPage.unknown')}
        />
      </div>

      <Card padding={false}>
        <CardHeader className="border-b border-border px-4 py-3 pb-3">
          <h3 className="font-medium text-foreground">{t('assetsPage.upcomingExpiry')}</h3>
        </CardHeader>
        {data.expiring.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">{t('assetsPage.noExpiringAssets')}</div>
        ) : (
          <div className="divide-y divide-border">
            {data.expiring.map((asset) => (
              <ExpiringRow key={`${asset.asset_type}-${asset.name}-${asset.expiry_date}`} asset={asset} />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function MetricCard({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <Card className={cn('p-4', highlight && 'border-warning bg-warning/10')} padding={false}>
      <div className={cn('text-xs text-muted-foreground', highlight && 'text-warning')}>{label}</div>
      <div className={cn('mt-1 text-lg font-semibold text-foreground', highlight && 'text-warning')}>{value}</div>
    </Card>
  );
}

function DistributionCard({
  title,
  data,
  unknown,
}: {
  title: string;
  data: Record<string, number>;
  unknown: string;
}) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);

  return (
    <Card className="p-4" padding={false}>
      <h3 className="mb-3 font-medium text-foreground">{title}</h3>
      <div className="space-y-2">
        {entries.map(([key, count]) => (
          <div key={key || 'unknown'} className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{key || unknown}</span>
            <span className="font-medium text-foreground">{count}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function ExpiringRow({ asset }: { asset: ExpiringAsset }) {
  return (
    <div className="px-4 py-3 flex items-center justify-between gap-4">
      <div className="min-w-0">
        <div className="truncate font-medium text-foreground">{asset.name}</div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          {asset.asset_type}
          {asset.provider ? ` / ${asset.provider}` : ''}
        </div>
      </div>
      <div className="flex items-center gap-3 text-sm flex-shrink-0">
        <span className="text-muted-foreground">{asset.expiry_date}</span>
        <SeverityBadge severity={asset.severity} days={asset.days_until_expiry} />
      </div>
    </div>
  );
}

function SeverityBadge({ severity, days }: { severity: ExpiringAsset['severity']; days: number }) {
  const { t } = useI18n();
  const label =
    severity === 'expired'
      ? t('assetsPage.expired')
      : severity === 'expiring'
        ? t('assetsPage.dueSoon')
        : t('assetsPage.normal');
  const variant = severity === 'expired' ? 'destructive' : severity === 'expiring' ? 'warning' : 'success';

  return (
    <Badge variant={variant}>
      {label} ({days}d)
    </Badge>
  );
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
